import React from "react";
import { useAuth } from "../../core/context/AuthContext";
import { InlineSpinner } from "./shared/SourceMessageAccordion";
import { getBusinessUserInitials } from "./shared/businessUserDisplay";
import {
  RECIPIENT_PROFILE_FIELD_DESCRIPTION,
  RECIPIENT_PROFILE_FIELD_PLACEHOLDER,
  RECIPIENT_PROFILE_INTRO_DESCRIPTION,
} from "./shared/performerRecipientProfile";

const PROFILE_DESCRIPTION_MAX_LENGTH = 240;

const ProfileRoleField = ({
  profileDescription,
  onProfileDescriptionChange,
  onProfileDescriptionBlur,
  textareaId,
  rows = 3,
}) => (
  <div className="business-settings-field settings-profile-field">
    <label className="settings-profile-field__title" htmlFor={textareaId}>
      Role & priorities
    </label>
    <p className="settings-profile-field__desc">{RECIPIENT_PROFILE_FIELD_DESCRIPTION}</p>
    <textarea
      id={textareaId}
      className="business-settings-textarea"
      rows={rows}
      value={profileDescription}
      onChange={(event) =>
        onProfileDescriptionChange?.(
          event.target.value.slice(0, PROFILE_DESCRIPTION_MAX_LENGTH)
        )
      }
      onBlur={() => onProfileDescriptionBlur?.()}
      placeholder={RECIPIENT_PROFILE_FIELD_PLACEHOLDER}
    />
    <p className="business-settings-hint">
      <span className="business-settings-hint__count">
        {profileDescription.length}/{PROFILE_DESCRIPTION_MAX_LENGTH}
      </span>
    </p>
  </div>
);

/**
 * Profile block inside unified settings.
 *
 * @example
 * <ActionItemsSettingsProfileSection
 *   variant="web-section"
 *   userName="Abhinav Khushraj"
 *   profileDescription="Founder focused on GTM"
 *   onProfileDescriptionChange={(v) => setProfileDescription(v)}
 * />
 */
const ActionItemsSettingsProfileSection = ({
  userName = "",
  userEmail = "",
  profileDescription = "",
  onProfileDescriptionChange,
  onProfileDescriptionBlur,
  setupProfileHighlight = false,
  onSkipProfileSetup,
  profileLoading = false,
  variant = "legacy",
}) => {
  const { user } = useAuth();
  const aiCreditsRemaining = Number(user?.ai_credits_remaining);
  const initials = getBusinessUserInitials(userName);
  const displayName = String(userName || "").trim() || "Your account";
  const textareaId =
    variant === "extension-piece" ? "settings-profile-extension" : "settings-profile-description";

  if (variant === "extension-piece") {
    return (
      <div className="settings-general-piece" id="settings-profile-section">
        <p className="settings-general-piece__desc">
          Include role, manager level (IC, manager, director, etc.), and priorities. Used when
          summarizing action items and drafting follow-ups.
        </p>
        <ProfileRoleField
          profileDescription={profileDescription}
          onProfileDescriptionChange={onProfileDescriptionChange}
          onProfileDescriptionBlur={onProfileDescriptionBlur}
          textareaId={textareaId}
          rows={4}
        />
      </div>
    );
  }

  if (variant === "web-section") {
    return (
      <section className="settings-general-section" id="settings-profile-section">
        {profileLoading ? (
          <div className="business-app-settings-block__loading">
            <InlineSpinner text="Loading profile…" />
          </div>
        ) : null}
        {setupProfileHighlight ? (
          <p className="business-settings-section__desc business-settings-section__desc--highlight">
            {RECIPIENT_PROFILE_INTRO_DESCRIPTION}
          </p>
        ) : null}
        <h3 className="settings-general-section__title">About you</h3>
        <div
          className={`business-settings-profile-header${
            profileLoading ? " business-app-settings-block__content--dimmed" : ""
          }`}
        >
          <div className="business-settings-avatar" aria-hidden="true">
            {initials}
          </div>
          <div>
            <div className="business-settings-profile-name">{displayName}</div>
            {userEmail ? (
              <div className="business-settings-profile-email">{userEmail}</div>
            ) : null}
            {Number.isFinite(aiCreditsRemaining) ? (
              <div className="business-settings-profile-credits">
                {aiCreditsRemaining} AI credits remaining
              </div>
            ) : null}
          </div>
        </div>
        <ProfileRoleField
          profileDescription={profileDescription}
          onProfileDescriptionChange={onProfileDescriptionChange}
          onProfileDescriptionBlur={onProfileDescriptionBlur}
          textareaId={textareaId}
        />
        {setupProfileHighlight && onSkipProfileSetup ? (
          <div className="business-settings-profile-skip">
            <button type="button" className="secondary-button" onClick={onSkipProfileSetup}>
              Skip for now
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <div className="settings-profile-flat" id="settings-profile-section">
      {profileLoading ? (
        <div className="business-app-settings-block__loading">
          <InlineSpinner text="Loading profile…" />
        </div>
      ) : null}
      {setupProfileHighlight ? (
        <p className="business-settings-section__desc business-settings-section__desc--highlight">
          {RECIPIENT_PROFILE_INTRO_DESCRIPTION}
        </p>
      ) : null}
      <div
        className={`business-settings-profile-header${profileLoading ? " business-app-settings-block__content--dimmed" : ""}`}
      >
        <div className="business-settings-avatar" aria-hidden="true">
          {initials}
        </div>
        <div>
          <div className="business-settings-profile-name">{displayName}</div>
          {userEmail ? (
            <div className="business-settings-profile-email">{userEmail}</div>
          ) : null}
          {Number.isFinite(aiCreditsRemaining) ? (
            <div className="business-settings-profile-credits">
              {aiCreditsRemaining} AI credits remaining
            </div>
          ) : null}
        </div>
      </div>
      <ProfileRoleField
        profileDescription={profileDescription}
        onProfileDescriptionChange={onProfileDescriptionChange}
        onProfileDescriptionBlur={onProfileDescriptionBlur}
        textareaId={textareaId}
      />
      {setupProfileHighlight && onSkipProfileSetup ? (
        <div className="business-settings-profile-skip">
          <button type="button" className="secondary-button" onClick={onSkipProfileSetup}>
            Skip for now
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default ActionItemsSettingsProfileSection;
